import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useCallback, useEffect, useRef } from 'react';
import { IMFPPStore } from '../../stores';
import { sendGtagEvent } from '@monorepo/tools/src/lib/tools/tracking';
import { TrackingActions } from '@monorepo/tools/src/lib/consts/tracking/actions';
import { EVENTS } from '@monorepo/tools/src/lib/consts/tracking/events';
import { useRefetchOnVisibilityAndRoute } from '../tools/use-refetch-on-visibility-and-route';
import { IAccount } from '../../models/account.model';

const ACCOUNT_STALE_THRESHOLD_MS = 5 * 60 * 1000;

export function useAccounts() {
	const { publisherStore } = useStores<IMFPPStore>();
	const accountHttpStore = publisherStore.getListStore();
	const lastAccountFetchRef = useRef(0);

	const applyAccounts = useCallback(
		(accounts: IAccount[]) => {
			if (!accounts || !accounts.length) {
				return;
			}
			publisherStore.getListStore().setData(accounts);
		},
		[publisherStore]
	);

	const refetchAccounts = useCallback(() => {
		if (Date.now() - lastAccountFetchRef.current < ACCOUNT_STALE_THRESHOLD_MS) {
			return;
		}
		accountHttpStore.fetch(undefined).then(accounts => {
			lastAccountFetchRef.current = Date.now();
			if (accounts?.length) {
				applyAccounts(accounts);
			}
		});
	}, [accountHttpStore, applyAccounts]);

	useEffect(() => {
		const startTime = Date.now();
		accountHttpStore.fetch(undefined).then(accounts => {
			lastAccountFetchRef.current = Date.now();
			if (!accounts || !accounts.length) {
				return;
			}
			const totalTime = ((Date.now() - startTime) / 1000).toFixed(2);
			sendGtagEvent({
				action: TrackingActions.ApiResponse,
				category: EVENTS.GENERAL.PAGE_LOAD_SPEED,
				label: window.location.href,
				value: totalTime,
			});
			applyAccounts(accounts);
		});
	}, [accountHttpStore, applyAccounts]);

	useRefetchOnVisibilityAndRoute(refetchAccounts, true);

	return {
		error: accountHttpStore.getHttpError(),
		isLoading: accountHttpStore.getIsLoading(),
		isSuccess: accountHttpStore.getIsSuccess(),
		accountList: accountHttpStore.getData(),
	};
}
